'use client';

import { useEffect } from 'react';

interface ToolContent {
  type: 'text' | 'image' | 'resource';
  text?: string;
  data?: string;
  mimeType?: string;
}

interface ToolResult {
  content: ToolContent[];
  isError?: boolean;
}

interface WebMCPTool {
  name: string;
  description: string;
  title?: string;
  inputSchema?: {
    type: 'object';
    properties?: Record<string, unknown>;
    required?: string[];
  };
  annotations?: {
    readOnlyHint?: boolean;
    untrustedContentHint?: boolean;
  };
  execute: (input: Record<string, unknown>) => Promise<ToolResult>;
}

interface ModelContextRegisterToolOptions {
  signal?: AbortSignal;
}

interface NavigatorModelContext {
  registerTool: (tool: WebMCPTool, options?: ModelContextRegisterToolOptions) => void;
}

declare global {
  interface Navigator {
    modelContext?: NavigatorModelContext;
  }
}

/**
 * WebMCP Provider
 *
 * Registers browser-accessible AI agent tools using the navigator.modelContext API.
 * Provides tools for querying services, blog posts, projects, and contact information.
 *
 * @see https://webmachinelearning.github.io/webmcp/
 */
const WebMCPProvider = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || !navigator.modelContext) {
      // eslint-disable-next-line no-console
      console.log('[WebMCP] navigator.modelContext not available');

      return undefined;
    }

    // eslint-disable-next-line no-console
    console.log('[WebMCP] Registering tools...');

    const abortController = new AbortController();

    try {
      navigator.modelContext.registerTool(
        {
          name: 'getServices',
          title: 'Get Workspace Services',
          description:
            'Retrieves a comprehensive list of digital product development services offered by Workspace, including Discovery, Design, Development, Delivery, and AI solutions.',
          inputSchema: {
            type: 'object',
            properties: {},
          },
          annotations: {
            readOnlyHint: true,
            untrustedContentHint: false,
          },
          execute: async () => {
            try {
              const services = [
                {
                  name: 'Discovery',
                  description: 'Product research, strategy, and requirements gathering',
                  url: `${window.location.origin}/services/discovery`,
                },
                {
                  name: 'Design',
                  description: 'UX/UI design, prototyping, and user experience optimization',
                  url: `${window.location.origin}/services/design`,
                },
                {
                  name: 'Development',
                  description: 'Full-stack web development with modern technologies',
                  url: `${window.location.origin}/services/development`,
                },
                {
                  name: 'Delivery',
                  description: 'Launch support, deployment, and post-launch maintenance',
                  url: `${window.location.origin}/services/delivery`,
                },
                {
                  name: 'AI Strategy & Discovery',
                  description: 'AI opportunity assessment and strategic planning',
                  url: `${window.location.origin}/services/ai-strategy-discovery`,
                },
                {
                  name: 'AI Search & Knowledge',
                  description: 'Enterprise search and knowledge management with AI',
                  url: `${window.location.origin}/services/ai-search-knowledge`,
                },
                {
                  name: 'AI Product Development',
                  description: 'Building AI-powered digital products and features',
                  url: `${window.location.origin}/services/ai-product-development`,
                },
                {
                  name: 'AI-Powered Automation',
                  description: 'Process automation and workflow optimization with AI',
                  url: `${window.location.origin}/services/ai-powered-automation`,
                },
              ];

              return {
                content: [
                  {
                    type: 'text',
                    text: JSON.stringify(
                      {
                        success: true,
                        services,
                        catalogUrl: `${window.location.origin}/services`,
                      },
                      null,
                      2
                    ),
                  },
                ],
              };
            } catch (error) {
              return {
                content: [
                  {
                    type: 'text',
                    text: `Error fetching services: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  },
                ],
                isError: true,
              };
            }
          },
        },
        { signal: abortController.signal }
      );

      navigator.modelContext.registerTool(
        {
          name: 'getBlogPosts',
          title: 'Get Blog Posts',
          description:
            'Retrieves recent blog posts from Workspace covering web development, AI integration, React, Next.js, and software engineering best practices.',
          inputSchema: {
            type: 'object',
            properties: {
              limit: {
                type: 'number',
                description: 'Maximum number of posts to return (default: 10)',
                default: 10,
                minimum: 1,
                maximum: 50,
              },
              category: {
                type: 'string',
                description: 'Filter by category: frontend, backend, design, or business',
                enum: ['frontend', 'backend', 'design', 'business'],
              },
            },
          },
          annotations: {
            readOnlyHint: true,
            untrustedContentHint: false,
          },
          execute: async args => {
            try {
              const limit = (args.limit as number) || 10;
              const category = args.category as string | undefined;

              const blogPosts = [
                {
                  title: 'Vibe Coding: From Prototype to Production',
                  slug: 'vibe-coding-prototype-to-production',
                  category: 'frontend',
                  date: '2025-01-15',
                },
                {
                  title: 'How Claude Skills Turn Team Expertise into Reusable AI Workflows',
                  slug: 'how-claude-skills-turn-team-expertise-into-reusable-ai-workflows',
                  category: 'business',
                  date: '2025-01-10',
                },
                {
                  title: 'AI-Driven Prototyping: From Idea to Working Product in Days',
                  slug: 'ai-driven-prototyping-from-idea-to-working-product-in-days',
                  category: 'business',
                  date: '2025-01-05',
                },
              ]
                .filter(post => !category || post.category === category)
                .slice(0, limit)
                .map(post => ({
                  ...post,
                  url: `${window.location.origin}/blog/${post.slug}`,
                }));

              return {
                content: [
                  {
                    type: 'text',
                    text: JSON.stringify(
                      {
                        success: true,
                        posts: blogPosts,
                        totalShown: blogPosts.length,
                        blogUrl: `${window.location.origin}/blog`,
                      },
                      null,
                      2
                    ),
                  },
                ],
              };
            } catch (error) {
              return {
                content: [
                  {
                    type: 'text',
                    text: `Error fetching blog posts: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  },
                ],
                isError: true,
              };
            }
          },
        },
        { signal: abortController.signal }
      );

      navigator.modelContext.registerTool(
        {
          name: 'getProjects',
          title: 'Get Portfolio Projects',
          description:
            'Retrieves portfolio projects and case studies showcasing digital products, web applications, and B2B software solutions built by Workspace.',
          inputSchema: {
            type: 'object',
            properties: {},
          },
          annotations: {
            readOnlyHint: true,
            untrustedContentHint: false,
          },
          execute: async () => {
            try {
              const projects = [
                {
                  name: 'Boat4You',
                  description: 'Charter booking platform for yacht rentals',
                  industry: 'Charter & Maritime',
                  url: `${window.location.origin}/work/boat4you`,
                },
                {
                  name: 'Serwizz CMMS',
                  description: 'Computerized maintenance management system',
                  industry: 'Enterprise Software',
                  url: `${window.location.origin}/work/serwizz-cmms`,
                },
                {
                  name: 'Capax Service Report App',
                  description: 'Service reporting and field management application',
                  industry: 'Field Services',
                  url: `${window.location.origin}/work/capax-service-report-app`,
                },
                {
                  name: 'DES Time Tracking',
                  description: 'Time tracking and project management tool',
                  industry: 'Productivity',
                  url: `${window.location.origin}/work/des-time-tracking`,
                },
                {
                  name: 'Sailweek',
                  description: 'Sailing event booking and management platform',
                  industry: 'Charter & Maritime',
                  url: `${window.location.origin}/work/sailweek`,
                },
              ];

              return {
                content: [
                  {
                    type: 'text',
                    text: JSON.stringify(
                      {
                        success: true,
                        projects,
                        portfolioUrl: `${window.location.origin}/work`,
                      },
                      null,
                      2
                    ),
                  },
                ],
              };
            } catch (error) {
              return {
                content: [
                  {
                    type: 'text',
                    text: `Error fetching projects: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  },
                ],
                isError: true,
              };
            }
          },
        },
        { signal: abortController.signal }
      );

      navigator.modelContext.registerTool(
        {
          name: 'getContactInfo',
          title: 'Get Contact Information',
          description:
            'Retrieves contact information for Workspace including company details, location, and how to get in touch.',
          inputSchema: {
            type: 'object',
            properties: {},
          },
          annotations: {
            readOnlyHint: true,
            untrustedContentHint: false,
          },
          execute: async () => {
            try {
              const contactInfo = {
                company: 'Workspace',
                tagline: 'Custom Digital Products & B2B Software Development',
                location: {
                  city: 'Split',
                  country: 'Croatia',
                  address: 'Poljička cesta 35, 21000 Split, Croatia',
                },
                website: window.location.origin,
                contactPage: `${window.location.origin}/contact`,
                aboutPage: `${window.location.origin}/about`,
                officePage: `${window.location.origin}/office`,
                careersPage: `${window.location.origin}/careers`,
              };

              return {
                content: [
                  {
                    type: 'text',
                    text: JSON.stringify(
                      {
                        success: true,
                        ...contactInfo,
                      },
                      null,
                      2
                    ),
                  },
                ],
              };
            } catch (error) {
              return {
                content: [
                  {
                    type: 'text',
                    text: `Error fetching contact info: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  },
                ],
                isError: true,
              };
            }
          },
        },
        { signal: abortController.signal }
      );

      // eslint-disable-next-line no-console
      console.log('[WebMCP] Successfully registered 4 tools');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[WebMCP] Failed to register tools:', error);
    }

    // Cleanup on unmount
    return () => {
      abortController.abort();
    };
  }, []);

  return null;
};

export default WebMCPProvider;
